import { useState, useEffect, createContext, useContext, ReactNode, useCallback, useRef } from 'react';
import { vendorSupabase as supabase } from '@/integrations/supabase/vendor-client';
import { Capacitor } from '@capacitor/core';
import { toast } from 'sonner';
import {
  VENDOR_SESSION_KEY,
  SESSION_INVALIDATED_EVENT,
  getVendorSessionId,
  setVendorSessionId,
} from '@/lib/vendorSession';
import {
  getCachedFcmToken,
  mintFreshFcmToken,
  setLastRegisteredFcm,
} from '@/hooks/useVendorFcmRegistration';

interface VendorProfile {
  id: string;
  name: string;
  phone: string;
  vendor_id: string | null;
  email: string | null;
  contact_person: string | null;
  active_session_id?: string | null;
}

interface VendorAuthContextType {
  vendor: VendorProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  sessionId: string | null;
  sendOtp: (phone: string) => Promise<{ success: boolean; error?: string; vendorName?: string }>;
  verifyOtp: (phone: string, otp: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
}

const VENDOR_STORAGE_KEY = 'vendor_profile_cache';
const VENDOR_AUTH_TOKEN_KEY = 'vendor-auth-token';
const FORCE_SIGNED_OUT_KEY = 'vendor_force_signed_out_at';
// Honour the force-signed-out flag only briefly after it's set. This
// lets a legitimate "signed out on other device" event stick across
// the immediate reload, but prevents an Android OOM kill 30 s+ later
// from being misinterpreted as a forced sign-out on the next cold
// start (which was the "crashed back to login" symptom).
const FORCE_SIGNED_OUT_TTL_MS = 15_000;

function isForceSignedOutActive(): boolean {
  try {
    const raw = localStorage.getItem(FORCE_SIGNED_OUT_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) { localStorage.removeItem(FORCE_SIGNED_OUT_KEY); return false; }
    if (Date.now() - ts > FORCE_SIGNED_OUT_TTL_MS) {
      localStorage.removeItem(FORCE_SIGNED_OUT_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function saveVendorToStorage(v: VendorProfile | null) {
  try {
    if (v) localStorage.setItem(VENDOR_STORAGE_KEY, JSON.stringify(v));
    else localStorage.removeItem(VENDOR_STORAGE_KEY);
  } catch { /* ignore */ }
}

function loadVendorFromStorage(): VendorProfile | null {
  try {
    const raw = localStorage.getItem(VENDOR_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const VendorAuthContext = createContext<VendorAuthContextType | undefined>(undefined);

export function VendorAuthProvider({ children }: { children: ReactNode }) {
  const [vendor, setVendorState] = useState<VendorProfile | null>(loadVendorFromStorage);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionIdState] = useState<string | null>(getVendorSessionId());

  const sessionChannelRef = useRef<any>(null);
  const forcedOutRef = useRef<boolean>(false);

  const setVendor = useCallback((v: VendorProfile | null) => {
    setVendorState(v);
    saveVendorToStorage(v);
  }, []);

  // ── Single-device session enforcement ──
  const forceSignOutForOtherDevice = useCallback(async () => {
    if (forcedOutRef.current) return;
    forcedOutRef.current = true;
    // localStorage (not sessionStorage) survives the immediate reload
    // we trigger below, but the TTL ensures a later WebView OOM kill
    // won't be mistaken for a forced sign-out.
    try { localStorage.setItem(FORCE_SIGNED_OUT_KEY, String(Date.now())); } catch { /* ignore */ }
    if (sessionChannelRef.current) {
      try { supabase.removeChannel(sessionChannelRef.current); } catch { /* ignore */ }
      sessionChannelRef.current = null;
    }
    try { localStorage.removeItem(VENDOR_AUTH_TOKEN_KEY); } catch { /* ignore */ }
    try { localStorage.removeItem(VENDOR_STORAGE_KEY); } catch { /* ignore */ }
    setLastRegisteredFcm(null);
    setVendorSessionId(null);
    setSessionIdState(null);
    setVendor(null);
    try { supabase.auth.signOut().catch(() => {}); } catch { /* ignore */ }
    toast.error('You were signed out because your account was used on another device.');
    if (typeof window !== 'undefined' && window.location.pathname !== '/vendor/login') {
      window.location.assign('/vendor/login');
    }
  }, [setVendor]);

  const loadVendorProfile = useCallback(async (vendorId: string): Promise<VendorProfile | null> => {
    if (forcedOutRef.current) return null;
    try {
      const { data, error } = await supabase.functions.invoke('vendor-data', {
        body: { action: 'profile', vendor_id: vendorId },
      });
      if (error || !data?.data) {
        console.error('[VendorAuth] Failed to load profile:', error);
        return null;
      }
      const v = data.data;
      return {
        id: v.id,
        name: v.name,
        phone: v.phone,
        vendor_id: v.vendor_id ?? null,
        email: v.email ?? null,
        contact_person: v.contact_person ?? null,
      };
    } catch (err) {
      console.error('[VendorAuth] Unexpected error loading profile:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    checkExistingSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setVendor(null);
        return;
      }
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        const meta = session?.user?.user_metadata;
        if (meta?.role === 'vendor' && !forcedOutRef.current) {
          setTimeout(() => {
            loadVendorProfile(meta.vendor_id).then(v => { if (v) setVendor(v); });
          }, 0);
        }
      }
    });

    const onInvalidated = () => { forceSignOutForOtherDevice(); };
    window.addEventListener(SESSION_INVALIDATED_EVENT, onInvalidated as EventListener);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener(SESSION_INVALIDATED_EVENT, onInvalidated as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Realtime + broadcast + resume/focus/online safety net.
  useEffect(() => {
    if (!vendor?.id) return;
    const vendorId = vendor.id;
    let cancelled = false;
    let channel: any = null;
    let broadcastChannel: any = null;
    let resubscribeTimer: ReturnType<typeof setTimeout> | null = null;
    let lastCheckAt = 0;

    const handleUpdate = (payload: any) => {
      const next = payload?.new?.active_session_id;
      const stored = localStorage.getItem(VENDOR_SESSION_KEY);
      if (next && stored && next !== stored) {
        forceSignOutForOtherDevice();
      }
    };

    const checkServerSession = async () => {
      if (cancelled || forcedOutRef.current) return;
      const stored = localStorage.getItem(VENDOR_SESSION_KEY);
      if (!stored) return;
      const now = Date.now();
      if (now - lastCheckAt < 2000) return;
      lastCheckAt = now;
      try {
        const res = await supabase.functions.invoke('vendor-data', {
          body: { action: 'validate_session', vendor_id: vendorId, session_id: stored },
        });
        if (res.data?.success && res.data.valid === false) {
          forceSignOutForOtherDevice();
        }
      } catch (err) {
        console.warn('[vendor-auth] validate_session failed', err);
      }
    };

    const subscribe = () => {
      if (cancelled) return;
      if (sessionChannelRef.current) {
        try { supabase.removeChannel(sessionChannelRef.current); } catch { /* ignore */ }
        sessionChannelRef.current = null;
      }
      channel = supabase
        .channel(`vendor-session-${vendorId}`)
        .on('postgres_changes' as any, {
          event: 'UPDATE',
          schema: 'public',
          table: 'vendors',
          filter: `id=eq.${vendorId}`,
        }, handleUpdate)
        .subscribe((status: string) => {
          if (status === 'SUBSCRIBED') {
            console.log('[vendor-auth] session realtime: SUBSCRIBED');
            checkServerSession();
          } else if (
            status === 'CHANNEL_ERROR' ||
            status === 'TIMED_OUT' ||
            status === 'CLOSED'
          ) {
            console.warn(`[vendor-auth] session realtime ${status} — resubscribing`);
            if (resubscribeTimer) clearTimeout(resubscribeTimer);
            resubscribeTimer = setTimeout(subscribe, 1500);
          }
        });
      sessionChannelRef.current = channel;
    };

    subscribe();

    const subscribeBroadcast = () => {
      if (cancelled) return;
      try {
        broadcastChannel = supabase
          .channel(`vendor-session:${vendorId}`)
          .on('broadcast' as any, { event: 'session_replaced' }, (msg: any) => {
            const next = msg?.payload?.new_session_id;
            const stored = localStorage.getItem(VENDOR_SESSION_KEY);
            if (next && stored && next !== stored) {
              forceSignOutForOtherDevice();
            }
          })
          .subscribe();
      } catch (err) {
        console.warn('[vendor-auth] broadcast subscribe failed', err);
      }
    };
    subscribeBroadcast();

    const removers: Array<() => void> = [];
    const onVisible = () => { if (document.visibilityState === 'visible') checkServerSession(); };
    const onFocus = () => checkServerSession();
    const onOnline = () => checkServerSession();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    removers.push(() => document.removeEventListener('visibilitychange', onVisible));
    removers.push(() => window.removeEventListener('focus', onFocus));
    removers.push(() => window.removeEventListener('online', onOnline));
    (async () => {
      try {
        if (!Capacitor.isNativePlatform()) return;
        const { App } = await import('@capacitor/app');
        const sub = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) checkServerSession();
        });
        removers.push(() => { try { sub.remove(); } catch { /* ignore */ } });
      } catch (err) {
        console.warn('[vendor-auth] resume listener setup failed', err);
      }
    })();

    return () => {
      cancelled = true;
      if (resubscribeTimer) clearTimeout(resubscribeTimer);
      try { if (sessionChannelRef.current) supabase.removeChannel(sessionChannelRef.current); } catch { /* ignore */ }
      sessionChannelRef.current = null;
      try { if (broadcastChannel) supabase.removeChannel(broadcastChannel); } catch { /* ignore */ }
      broadcastChannel = null;
      for (const r of removers) { try { r(); } catch { /* ignore */ } }
    };
  }, [vendor?.id, forceSignOutForOtherDevice]);

  const checkExistingSession = async () => {
    try {
      if (isForceSignedOutActive()) {
        try { localStorage.removeItem(VENDOR_AUTH_TOKEN_KEY); } catch { /* ignore */ }
        try { localStorage.removeItem(VENDOR_STORAGE_KEY); } catch { /* ignore */ }
        setVendorSessionId(null);
        setSessionIdState(null);
        setVendor(null);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.user_metadata?.role === 'vendor') {
        const vendorId = session.user.user_metadata.vendor_id;
        const cached = loadVendorFromStorage();

        // IMPORTANT: do NOT block the cold-start sign-in on a
        // validate_session HTTP round-trip. On Android, after an OOM
        // kill, the WebView restarts before networking is fully ready,
        // and a transient failure was being treated as "session
        // replaced" — dumping the user back to the login screen even
        // though they are the legitimate active device.
        //
        // The realtime channel + broadcast listener set up in the
        // effect below is the authoritative single-device enforcement
        // path. It only signs the user out when the server actively
        // emits a session_replaced event, which is exactly the
        // behaviour we want.
        if (cached && cached.id === vendorId) {
          setVendorState(cached);
          setLoading(false);
          // Refresh profile data in the background; never sign out on failure.
          loadVendorProfile(vendorId).then(fresh => { if (fresh) setVendor(fresh); });
          return;
        }

        const fresh = await loadVendorProfile(vendorId);
        if (fresh) setVendor(fresh);
        else setVendor(null);
      } else {
        setVendor(null);
      }
    } finally {
      setLoading(false);
      // Tell the inline HTML splash it can disappear now that the
      // initial session check is done. Safe to call multiple times.
      try { (window as any).__lovHideSplash?.(); } catch { /* ignore */ }
    }
  };

  const sendOtp = async (phone: string) => {
    const { data, error } = await supabase.functions.invoke('vendor-login', {
      body: { phone, action: 'send_otp' },
    });
    if (error || !data?.success) {
      return { success: false, error: data?.error || 'Failed to send OTP' };
    }
    return { success: true, vendorName: data.vendor_name };
  };

  const verifyOtp = async (phone: string, otp: string) => {
    let mintedFcm: { token: string; platform: string } | null = null;
    try { mintedFcm = await mintFreshFcmToken(); } catch { mintedFcm = null; }

    const { data, error } = await supabase.functions.invoke('vendor-login', {
      body: {
        phone,
        action: 'verify_otp',
        otp_code: otp,
        fcm_token: mintedFcm?.token ?? null,
        platform: mintedFcm?.platform ?? null,
      },
    });

    if (error || !data?.success) {
      return { success: false, error: data?.error || 'Verification failed' };
    }

    // Install session by writing directly to localStorage to avoid the
    // flaky inline /auth/v1/user round-trip in supabase.auth.setSession().
    try {
      const accessToken: string = data.access_token;
      const refreshToken: string = data.refresh_token;
      const [, payloadB64] = accessToken.split('.');
      const payload = JSON.parse(
        atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'))
      );
      const nowSec = Math.floor(Date.now() / 1000);
      const expiresAt: number = typeof payload.exp === 'number' ? payload.exp : nowSec + 3600;
      const session = {
        access_token: accessToken,
        refresh_token: refreshToken,
        token_type: 'bearer',
        expires_at: expiresAt,
        expires_in: Math.max(0, expiresAt - nowSec),
        provider_token: null,
        provider_refresh_token: null,
        user: {
          id: payload.sub,
          aud: payload.aud ?? 'authenticated',
          role: payload.role ?? 'authenticated',
          email: payload.email ?? null,
          phone: payload.phone ?? '',
          app_metadata: payload.app_metadata ?? { provider: 'email', providers: ['email'] },
          user_metadata: payload.user_metadata ?? {},
          identities: [],
          created_at: new Date(nowSec * 1000).toISOString(),
          updated_at: new Date(nowSec * 1000).toISOString(),
        },
      };
      try {
        localStorage.setItem(VENDOR_AUTH_TOKEN_KEY, JSON.stringify(session));
      } catch (storageErr) {
        console.error('[vendor-auth] failed to persist session to storage', storageErr);
        return { success: false, error: 'Failed to establish session' };
      }
      try { (supabase as any).realtime?.setAuth?.(accessToken); } catch { /* ignore */ }
      setTimeout(() => {
        supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        }).catch(() => { /* storage already has it */ });
      }, 0);
    } catch (err) {
      console.error('[vendor-auth] verifyOtp session install failed', err);
      return { success: false, error: 'Failed to establish session' };
    }

    if (data.session_id) {
      setVendorSessionId(data.session_id);
      setSessionIdState(data.session_id);
    }
    forcedOutRef.current = false;
    try { localStorage.removeItem(FORCE_SIGNED_OUT_KEY); } catch { /* ignore */ }

    const v = data.vendor;
    setVendor({
      id: v.id,
      name: v.name,
      phone: v.phone,
      vendor_id: v.vendor_id ?? null,
      email: v.email ?? null,
      contact_person: v.contact_person ?? null,
      active_session_id: data.session_id ?? null,
    });

    if (mintedFcm?.token && data.vendor?.id) {
      setLastRegisteredFcm({ vendorId: data.vendor.id, token: mintedFcm.token });
    }

    return { success: true };
  };

  const signOut = async () => {
    // Detach Capacitor push listeners best-effort.
    if (Capacitor.isNativePlatform()) {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        await PushNotifications.removeAllListeners();
      } catch { /* ignore */ }
    }
    // Soft-deactivate ONLY this device's push_tokens row scoped by token.
    try {
      const cachedToken = getCachedFcmToken();
      if (vendor?.id && cachedToken) {
        await (supabase.from('push_tokens' as any) as any)
          .update({ is_active: false, updated_at: new Date().toISOString() })
          .eq('user_id', vendor.id)
          .eq('user_type', 'vendor')
          .eq('token', cachedToken);
      }
    } catch (err) {
      console.error('[vendor-auth] push_tokens deactivate failed (continuing):', err);
    }

    await supabase.auth.signOut();
    setVendor(null);
    setVendorSessionId(null);
    setSessionIdState(null);
    setLastRegisteredFcm(null);
  };

  return (
    <VendorAuthContext.Provider value={{
      vendor,
      loading,
      isAuthenticated: !!vendor,
      sessionId,
      sendOtp,
      verifyOtp,
      signOut,
    }}>
      {children}
    </VendorAuthContext.Provider>
  );
}

export function useVendorAuth() {
  const context = useContext(VendorAuthContext);
  if (!context) throw new Error('useVendorAuth must be used within VendorAuthProvider');
  return context;
}
